import { CSSInterpolation } from '@emotion/css/create-instance'
import { css } from '@emotion/react'
import React from 'react'

import { UnstyledButton } from '../../components/UnstyledButton'
import { CssVars } from '../themes'
import { ForegroundColorPrimaryHoverState } from './HoverState'
import { Type } from './Type'

type Props = {
  /**
   * Callback called when the user selects a new tab. Passed the ID of the newly-selected tab.
   */
  onChange: (id: string) => void
  /**
   * The ID of the selected tab.
   */
  selectedTabId: string
  /**
   * The array of tabs to display.
   *
   * `id`: The ID for this tab, used to track selected state.
   *
   * `name`: The text to display for the tab.
   */
  tabs: Array<{
    id: string
    name: string
  }>
  /**
   * Pass a CSS object if you need to override styles on the root element. Please do this sparingly.
   */
  overrideRootStyles?: CSSInterpolation
}

export function Tabs({
  onChange,
  overrideRootStyles,
  selectedTabId,
  tabs,
}: Props) {
  if (!tabs.length) return null
  return (
    <nav
      css={css([
        {
          display: 'flex',
        },
        overrideRootStyles,
      ])}
      role="tablist"
    >
      {tabs.map(group => {
        const isActive = selectedTabId === group.id
        return (
          <UnstyledButton
            key={group.id}
            aria-selected={isActive ? 'true' : 'false'}
            css={[
              {
                ':first-of-type': {
                  marginLeft: 0,
                },
                borderBottom: `solid 2px ${
                  isActive ? CssVars.semantic.primary.base : 'transparent'
                }`,
                display: 'inline-block',
                margin: `0 ${CssVars.size.gutterWidth}`,
                paddingBottom: CssVars.size.gutterWidth,
              },
            ]}
            onClick={() => {
              onChange(group.id)
            }}
            role="tab"
          >
            {isActive ? (
              <Type.Text isBold size="S">
                {group.name}
              </Type.Text>
            ) : (
              <ForegroundColorPrimaryHoverState>
                {({ hoverableClassName }) => (
                  <span
                    className={hoverableClassName}
                    css={{ cursor: 'pointer' }}
                  >
                    <Type.Text isBold size="S">
                      {group.name}
                    </Type.Text>
                  </span>
                )}
              </ForegroundColorPrimaryHoverState>
            )}
          </UnstyledButton>
        )
      })}
    </nav>
  )
}
